/**
 * UnreadStatusManager - 未讀狀態管理器
 * 
 * 設計原則：
 * 1. 單一真相來源（Single Source of Truth）
 * 2. 明確的狀態來源追蹤
 * 3. 集中的保護和防抖邏輯
 * 4. 自動持久化
 * 
 * @author AI Assistant
 * @date 2025-12-08
 */

import axios from 'axios';
import url from '../url';

// 狀態來源類型
const SOURCE = {
  LOCAL: 'local',   // 用戶剛點擊標記已讀，15秒內有效
  CACHE: 'cache',   // 從內存緩存讀取，30秒內有效（已移除 localStorage 持久化）
  API: 'api'        // 從後端獲取，30秒內有效
};

// TTL 配置
const TTL = {
  LOCAL: 15 * 1000,      // 15 秒（用户主动标记已读）
  CACHE: 30 * 1000,      // 30 秒（从内存缓存读取，降低不一致风险）
  API: 30 * 1000         // 30 秒（从 API 获取）
};

/**
 * 未讀狀態對象
 * @typedef {Object} UnreadStatus
 * @property {boolean} hasUnread - 是否有未讀
 * @property {number} lastUpdatedAt - 最後更新時間戳
 * @property {number} lastReadAt - 最後閱讀時間戳
 * @property {string} source - 狀態來源 ('local' | 'cache' | 'api')
 */

export default class UnreadStatusManager {
  /**
   * @param {string} userId - 用戶 ID
   */
  constructor(userId) {
    if (!userId) {
      throw new Error('UnreadStatusManager: userId is required');
    }
    
    this.userId = userId;
    this.statusMap = new Map(); // groupId -> UnreadStatus (僅內存，不持久化)
    this.updateLocks = new Map(); // groupId -> Promise<void>
    this.subscribers = new Set(); // (groupId, hasUnread) => void
    
    // 移除 localStorage 載入，避免多標籤頁/多設備數據不一致
    // this.loadFromStorage();
  }
  
  /**
   * 訂閱狀態變化
   * @param {Function} callback - 回調函數 (groupId, hasUnread) => void
   * @returns {Function} 取消訂閱函數
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }
  
  /**
   * 通知訂閱者
   * @private
   */
  _notify(groupId, hasUnread) {
    this.subscribers.forEach(callback => {
      try {
        callback(groupId, hasUnread);
      } catch (error) {
        console.error('[UnreadStatusManager] Subscriber error:', error);
      }
    });
  }
  
  /**
   * 獲取未讀狀態（自動處理 cache/fetch）
   * @param {number} groupId - 群組 ID
   * @returns {Promise<boolean>} 是否有未讀
   */
  async getStatus(groupId) {
    const gid = Number(groupId);
    if (Number.isNaN(gid)) {
      console.warn('[UnreadStatusManager] Invalid groupId:', groupId);
      return false;
    }
    
    const status = this.statusMap.get(gid);
    
    // 如果有有效的本地狀態，直接返回
    if (status && this._isValid(status)) {
      return status.hasUnread;
    }
    
    // 否則從後端獲取
    try {
      return await this._fetchFromBackend(gid);
    } catch (error) {
      console.error('[UnreadStatusManager] Failed to fetch status:', error);
      // 降級：返回 cache 狀態（即使過期）
      return status?.hasUnread ?? false;
    }
  }
  
  /**
   * 批量獲取未讀狀態
   * @param {number[]} groupIds - 群組 ID 列表
   * @returns {Promise<Object<number, boolean>>} groupId -> hasUnread 映射
   */
  async getStatusBatch(groupIds) {
    const gids = groupIds
      .filter(g => g != null && g !== '') // 先過濾 null/undefined/空字串
      .map(g => Number(g))
      .filter(g => !Number.isNaN(g) && g > 0); // 過濾 NaN 和非正數
    
    if (gids.length === 0) {
      return {};
    }
    
    // 分離有效和需要 fetch 的 groupIds
    const valid = {};
    const toFetch = [];
    
    gids.forEach(gid => {
      const status = this.statusMap.get(gid);
      if (status && this._isValid(status)) {
        valid[gid] = status.hasUnread;
      } else {
        toFetch.push(gid);
      }
    });
    
    // 批量 fetch 缺失的狀態
    let fetched = {};
    if (toFetch.length > 0) {
      try {
        fetched = await this._fetchFromBackendBatch(toFetch);
      } catch (error) {
        console.error('[UnreadStatusManager] Failed to fetch batch:', error);
        // 降級：使用 cache 狀態
        toFetch.forEach(gid => {
          const status = this.statusMap.get(gid);
          fetched[gid] = status?.hasUnread ?? false;
        });
      }
    }
    
    return { ...valid, ...fetched };
  }
  
  /**
   * 標記為已讀（樂觀更新）
   * @param {number} groupId - 群組 ID
   */
  markAsRead(groupId) {
    const gid = Number(groupId);
    if (Number.isNaN(gid)) {
      console.warn('[UnreadStatusManager] Invalid groupId:', groupId);
      return;
    }
    
    const now = Date.now();
    const status = {
      hasUnread: false,
      lastUpdatedAt: now,
      lastReadAt: now,
      source: SOURCE.LOCAL
    };
    
    this.statusMap.set(gid, status);
    // 移除持久化
    // this._persist(gid, status);
    this._notify(gid, false);
    
    console.log(`[UnreadStatusManager] Marked group ${gid} as read (local)`);
  }
  
  /**
   * 更新後端 lastReadTime（防抖處理）
   * @param {number} groupId - 群組 ID
   * @param {number} [activityId] - 活動 ID（可選）
   * @returns {Promise<void>}
   */
  async updateBackend(groupId, activityId = null) {
    const gid = Number(groupId);
    if (Number.isNaN(gid)) {
      console.warn('[UnreadStatusManager] Invalid groupId:', groupId);
      return;
    }
    
    // 檢查是否需要防抖
    if (this._shouldDebounce(gid)) {
      console.log(`[UnreadStatusManager] Debounced update for group ${gid}`);
      // 即使防抖，也要確保本地狀態是已讀
      this.markAsRead(gid);
      return;
    }
    
    // 等待任何進行中的請求
    const existingLock = this.updateLocks.get(gid);
    if (existingLock) {
      console.log(`[UnreadStatusManager] Waiting for existing update for group ${gid}`);
      await existingLock;
      return;
    }
    
    // 發送 API 請求
    const lock = this._doUpdate(gid, activityId);
    this.updateLocks.set(gid, lock);
    
    try {
      await lock;
    } finally {
      this.updateLocks.delete(gid);
    }
  }
  
  /**
   * 清除指定群組的狀態
   * @param {number} groupId - 群組 ID
   */
  clearStatus(groupId) {
    const gid = Number(groupId);
    if (Number.isNaN(gid)) return;
    
    this.statusMap.delete(gid);
    // 移除 localStorage 操作
    // this._removeFromStorage(gid);
  }
  
  /**
   * 清除所有狀態
   */
  clearAll() {
    this.statusMap.clear();
    // 移除 localStorage 操作
    // this._clearAllStorage();
  }
  
  // ==================== 私有方法 ====================
  
  /**
   * 判斷狀態是否有效
   * @private
   */
  _isValid(status) {
    if (!status) return false;
    
    const age = Date.now() - status.lastUpdatedAt;
    
    switch (status.source) {
      case SOURCE.LOCAL:
        return age < TTL.LOCAL;
      case SOURCE.CACHE:
        return age < TTL.CACHE;
      case SOURCE.API:
        return age < TTL.API;
      default:
        return false;
    }
  }
  
  /**
   * 判斷是否需要防抖
   * @private
   */
  _shouldDebounce(groupId) {
    console.log(`[UnreadStatusManager] Checking debounce for group ${groupId}`);
    const status = this.statusMap.get(groupId);
    if (!status || !status.lastReadAt) {
      console.log(`[UnreadStatusManager] No status or lastReadAt for group ${groupId}`);
      return false;
    }
    else {
      console.log(`[UnreadStatusManager] Status: ${JSON.stringify(status)}`);
    }
    
    const timeSinceLastRead = Date.now() - status.lastReadAt;
    return timeSinceLastRead < TTL.LOCAL;
  }
  
  /**
   * 從後端獲取單個群組狀態（內部改為調用批量 API）
   * @private
   */
  async _fetchFromBackend(groupId) {
    // 統一使用批量 API，即使只有一個 groupId
    const results = await this._fetchFromBackendBatch([groupId]);
    return results[groupId] ?? false;
  }
  
  /**
   * 從後端批量獲取群組狀態
   * @private
   */
  async _fetchFromBackendBatch(groupIds) {
    const headers = this._buildAuthHeaders();
    // 統一使用 groupIds 參數格式，不再使用單個 groupId
    const query = `groupIds=${groupIds.join(',')}`;
    const apiUrl = `${url.backendHost}api/messages/unread-status?${query}`;
    
    const response = await axios.get(apiUrl, {
      headers,
      withCredentials: true
    });
    
    // 統一返回批量格式 { byGroup: { groupId: hasUnread, ... } }
    const results = response.data?.byGroup || {};
    
    // 更新所有狀態
    const now = Date.now();
    Object.entries(results).forEach(([gid, hasUnread]) => {
      const groupId = Number(gid);
      const status = {
        hasUnread: Boolean(hasUnread),
        lastUpdatedAt: now,
        lastReadAt: hasUnread ? 0 : now,
        source: SOURCE.API
      };
      
      this.statusMap.set(groupId, status);
      // 移除持久化
      // this._persist(groupId, status);
      this._notify(groupId, status.hasUnread);
    });
    
    return results;
  }
  
  /**
   * 實際執行後端更新
   * @private
   */
  async _doUpdate(groupId, activityId) {
    const headers = this._buildAuthHeaders();
    const apiUrl = `${url.backendHost}api/messages/groups/${groupId}/last-read`;
    const body = activityId ? { activityId } : {};
    
    await axios.put(apiUrl, body, {
      headers,
      withCredentials: true
    });
    
    // 更新本地狀態
    const now = Date.now();
    const status = {
      hasUnread: false,
      lastUpdatedAt: now,
      lastReadAt: now,
      source: SOURCE.LOCAL
    };
    
    this.statusMap.set(groupId, status);
    // 移除持久化
    // this._persist(groupId, status);
    this._notify(groupId, false);
    
    console.log(`[UnreadStatusManager] Updated backend for group ${groupId}`);
  }
  
  /**
   * 構建認證 headers
   * @private
   */
  _buildAuthHeaders() {
    const jwtToken = localStorage.getItem('jwtToken');
    return jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
  }
  
  /**
   * 構建 localStorage key
   * @private
   */
  _buildStorageKey(groupId) {
    return `unread_v2_${groupId}_${this.userId}`;
  }
  
  /**
   * 從 localStorage 載入狀態
   * @private
   */
  loadFromStorage() {
    try {
      Object.keys(localStorage).forEach(key => {
        // 只載入新版本的 key（unread_v2_）
        if (!key.startsWith(`unread_v2_`) || !key.endsWith(`_${this.userId}`)) {
          return;
        }
        
        const raw = localStorage.getItem(key);
        if (!raw) return;
        
        const data = JSON.parse(raw);
        if (!data || typeof data.hasUnread !== 'boolean') return;
        
        // 提取 groupId
        const parts = key.split('_');
        const groupId = Number(parts[2]);
        if (Number.isNaN(groupId)) return;
        
        // 標記為 cache 來源
        const status = {
          hasUnread: data.hasUnread,
          lastUpdatedAt: data.lastUpdatedAt || Date.now(),
          lastReadAt: data.lastReadAt || 0,
          source: SOURCE.CACHE
        };
        
        this.statusMap.set(groupId, status);
      });
      
      console.log(`[UnreadStatusManager] Loaded ${this.statusMap.size} statuses from storage`);
    } catch (error) {
      console.error('[UnreadStatusManager] Failed to load from storage:', error);
    }
  }
  
  /**
   * 持久化狀態到 localStorage
   * @private
   */
  _persist(groupId, status) {
    try {
      const key = this._buildStorageKey(groupId);
      const data = {
        hasUnread: status.hasUnread,
        lastUpdatedAt: status.lastUpdatedAt,
        lastReadAt: status.lastReadAt
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('[UnreadStatusManager] Failed to persist:', error);
    }
  }
  
  /**
   * 從 localStorage 移除指定狀態
   * @private
   */
  _removeFromStorage(groupId) {
    try {
      const key = this._buildStorageKey(groupId);
      localStorage.removeItem(key);
    } catch (error) {
      console.error('[UnreadStatusManager] Failed to remove from storage:', error);
    }
  }
  
  /**
   * 清除所有 localStorage 狀態
   * @private
   */
  _clearAllStorage() {
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`unread_v2_`) && key.endsWith(`_${this.userId}`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('[UnreadStatusManager] Failed to clear storage:', error);
    }
  }
}
